import React, { useMemo } from 'react'
import BigNumber from 'bignumber.js'
import { Flex, Text, Heading, Image, Skeleton } from 'vvs-uikit'
import styled from 'styled-components'
import { useTranslation } from 'contexts/Localization'
import Balance from 'components/Balance'
import LocalBalance from 'components/LocalBalance'
import { useFarms, usePollFarmsWithUserData, usePriceVvsUsdc, useTokenPricesUsdc } from 'state/farms/hooks'
import { getAddress } from 'utils/addressHelpers'
import { getFarmApr, getFarmAprV2 } from 'utils/apr'
import { BIG_ZERO } from 'utils/bigNumber'
import { getBalanceAmount } from 'utils/formatBalance'
import { isBlindMode } from 'utils'
import { useRemovalOrders } from 'state/burn/hooks'
import { FIXED_REMOVAL_FEE_PECENTAGE } from 'config/constants/burn'

export const Wrapper = styled(Flex)`
  position: relative;
  background: ${({ theme }) => theme.colors.cardsBackground};
  box-shadow: 0px 10px 50px -20px rgba(121, 121, 121, 0.1);
  padding: 24px;
  border-radius: ${({ theme }) => theme.radii.card};
  width: 100%;
  overflow: hidden;
  > .info {
    position: relative;
    z-index: 20;
  }
  > .bg {
    position: absolute;
    z-index: 0;
    right: 18px;
    bottom: -60px;
    pointer-events: none;
  }
  > .bg-farms {
    bottom: -74px;
  }
`

const FarmsCard = () => {
  const { t } = useTranslation()
  const { data: farmsLP } = useFarms()
  const tokenPrices = useTokenPricesUsdc(farmsLP)
  const vvsPrice = usePriceVvsUsdc()

  usePollFarmsWithUserData(true)
  // const activeFarms = farmsLP.filter(farm => farm.pid !== 0 && farm.multiplier !== '0X' && !isArchivedPid(farm.pid))
  const farms = farmsLP.filter((farm) => farm.pid !== 0) // exclude VVS (pid:0) and include finished farms
  const stakedOnlyFarms = farms.filter(
    (farm) => farm.userData && new BigNumber(farm.userData.stakedBalance).isGreaterThan(0),
  )
  let totalStaked = BIG_ZERO
  let totalWeightedApr = BIG_ZERO
  stakedOnlyFarms.forEach((farm) => {
    if (farm.lpTotalSupply.gt(0) && farm.lpTotalInQuoteToken.gt(0)) {
      const valueOfBaseTokenInFarm = new BigNumber(farm.tokenPriceUsdc).times(farm.tokenAmountTotal)
      const overallValueOfAllTokensInFarm = valueOfBaseTokenInFarm.times(2)
      const totalLpTokens = getBalanceAmount(farm.lpTotalSupply)
      const lpPrice = overallValueOfAllTokensInFarm.div(totalLpTokens)
      const staked = getBalanceAmount(lpPrice.times(farm.userData.stakedBalance))
      const totalLiquidity = new BigNumber(farm.lpTotalInQuoteToken).times(farm.quoteTokenPriceUsdc)
      let tokenRewardsApr = 0
      let lpRewardsApr = 0
      if (farm.version === 2) {
        const { tokenRewardsApr: tapr, lpRewardsApr: lapr } = getFarmAprV2(
          farm.poolWeight,
          vvsPrice,
          farm.rewardData,
          totalLiquidity,
          getAddress(farm.lpAddresses),
          tokenPrices,
        )
        tokenRewardsApr = tapr
        lpRewardsApr = lapr
      } else {
        const { vvsRewardsApr: tapr, lpRewardsApr: lapr } = getFarmApr(
          farm.poolWeight,
          vvsPrice,
          totalLiquidity,
          getAddress(farm.lpAddresses),
        )
        tokenRewardsApr = tapr
        lpRewardsApr = lapr
      }
      totalStaked = totalStaked.plus(staked)
      totalWeightedApr = totalWeightedApr.plus(staked.times(tokenRewardsApr + lpRewardsApr))
    }
  })

  const orders = useRemovalOrders()
  const userDepositedRemovalOrders = useMemo(
    () => orders.filter((o) => o.userData && o.userData.stakedBalance.isGreaterThan('0')),
    [orders],
  )
  userDepositedRemovalOrders
    .filter((r) => farmsLP.some((f) => getAddress(f.lpAddresses) === r.lpAddress))
    .forEach((r) => {
      const targetFarm = farmsLP.find((f) => getAddress(f.lpAddresses) === r.lpAddress)
      if (r?.userData.stakedBalance.gt(0) && targetFarm.lpTotalSupply.gt(0) && targetFarm.lpTotalInQuoteToken.gt(0)) {
        const valueOfBaseTokenInFarm = new BigNumber(targetFarm.tokenPriceUsdc).times(targetFarm.tokenAmountTotal)
        const overallValueOfAllTokensInFarm = valueOfBaseTokenInFarm.times(2)
        const totalLpTokens = getBalanceAmount(targetFarm.lpTotalSupply)
        const lpPrice = overallValueOfAllTokensInFarm.div(totalLpTokens)
        const staked = getBalanceAmount(lpPrice.times(r.userData.stakedBalance))
        const totalLiquidity = new BigNumber(targetFarm.lpTotalInQuoteToken).times(targetFarm.quoteTokenPriceUsdc)
        const { vvsRewardsApr, lpRewardsApr } = getFarmApr(
          new BigNumber(targetFarm.poolWeight),
          vvsPrice,
          totalLiquidity,
          getAddress(targetFarm.lpAddresses),
        )
        const adjustedApr = (vvsRewardsApr + lpRewardsApr) * ((100 - FIXED_REMOVAL_FEE_PECENTAGE) / 100)
        totalStaked = totalStaked.plus(staked)
        totalWeightedApr = totalWeightedApr.plus(staked.times(adjustedApr))
      }
    })

  return (
    <Wrapper mt={['24px', null, null, '36px']} mb={['24px', null, null, '36px']} flexDirection="column">
      <Flex alignItems="center" justifyContent="flex-start">
        <Image src="/images/dashboard/seedling-icon.svg" width={32} height={32} mr="6px" />
        <Heading color="primary">{t('Crystal Farms')}</Heading>
      </Flex>
      <Flex className="info" justifyContent={['space-between', null, null, 'flex-start']}>
        <Flex flexDirection="column" mt="10px" mr="24px">
          <Text minWidth={['110px', null, null, null]} color="white" bold fontSize="20px">
            <LocalBalance
              decimals={totalStaked.gt(0) ? 2 : 0}
              fontSize="20px"
              color="white"
              bold
              prefix={totalStaked.gt(0) ? '~' : ''}
              lineHeight="1.1"
              value={totalStaked}
            />
          </Text>
          <Text minWidth={['110px', null, null, null]} color="grey" fontSize="13px">
            {t('Total Staked')}
          </Text>
        </Flex>
        <Flex flexDirection="column" mt="10px">
          <Text minWidth={['110px', null, null, null]} color="white" bold fontSize="20px">
            {isBlindMode() ? (
              <Skeleton width={60} />
            ) : (
              <Balance
                decimals={totalWeightedApr.gt(0) ? 2 : 0}
                fontSize="20px"
                color="white"
                bold
                unit="%"
                lineHeight="1.1"
                value={totalStaked.gt(0) ? totalWeightedApr.div(totalStaked).toNumber() : 0}
              />
            )}
          </Text>
          <Text minWidth={['110px', null, null, null]} color="grey" fontSize="13px">
            {t('Average APR')}
          </Text>
        </Flex>
      </Flex>
      {/* {isDesktop && <Image className="bg bg-farms" src="/images/dashboard/farms-bg.png" width={220} height={170} />} */}
    </Wrapper>
  )
}

export default FarmsCard
